import flattenDeep from 'lodash/flattenDeep';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { navigate } from '@/utils/navigation-service';

import {
  type Action,
  type CommandBarAction,
  type CommandBarContext,
  type Optional,
} from './types';

function resolve<T>(value: any, context: CommandBarContext): T {
  return typeof value === 'function' ? value(context) : value;
}

export function createAction(definition: Optional<Action, 'id'>): Action {
  return {
    ...definition,
    perform: definition.perform
      ? (context) => {
          return definition.perform?.(context);
        }
      : undefined,
    id: definition.id ?? uuidv4(),
  };
}

export function actionToKBar(
  action: Action,
  context: CommandBarContext
): CommandBarAction[] {
  if (typeof action.visible === 'function' && !action.visible(context)) {
    return [];
  }

  const resolvedIcon = resolve<React.ReactElement>(action.icon, context);
  const resolvedChildren = resolve<Action[]>(action.children, context);
  const resolvedSection = resolve<string>(action.section, context);
  const resolvedName = resolve<string>(action.name, context);
  const resolvedPlaceholder = resolve<string>(action.placeholder, context);
  const resolvedSubtitle = resolve<string>(action.subtitle, context);
  const resolvedIsSearchAction = resolve<boolean>(
    action.isSearchResult,
    context
  );
  const children = resolvedChildren
    ? flattenDeep(resolvedChildren.map((a) => actionToKBar(a, context))).filter(
        (a) => !!a
      )
    : [];

  const sectionPriority =
    typeof action.section !== 'string' && 'priority' in action.section
      ? ((action.section.priority as number) ?? 0)
      : 0;

  return [
    {
      id: action.id,
      name: resolvedName,
      section: resolvedSection,
      placeholder: resolvedPlaceholder,
      subtitle: resolvedSubtitle,
      keywords: action.keywords ?? '',
      shortcut: action.shortcut || [],
      icon: resolvedIcon,
      priority: (1 + (action.priority ?? 0)) * (1 + (sectionPriority ?? 0)),
      perform:
        action.perform || action.to
          ? () => performAction(action, context)
          : undefined,
      isSearchResult: resolvedIsSearchAction,
      keepOpen: action.keepOpen,
      contextKind: action.contextKind,
      selected: action.selected ? action.selected(context) : undefined,
      dangerous: action.dangerous,
      meta: action.meta,
      date: action.date,
      preview: action.preview,
      match: action.match,
    },
  ].concat(
    // @ts-ignore No overload matches this call
    children.map((child) => ({ ...child, parent: child.parent ?? action.id }))
  );
}

export async function performAction(
  action: Action,
  context: CommandBarContext
) {
  const result = action.perform
    ? action.perform(context)
    : action.to
      ? typeof action.to === 'string'
        ? navigate({ to: action.to })
        : window.open(action.to.url, action.to.target)
      : undefined;

  if (result instanceof Promise) {
    return result.catch((err: Error) => {
      toast.error(err.message);
    });
  }

  return result;
}

export enum Section {
  Navigation = 'Navigation',
  Settings = 'Settings',
  Chat = 'Chat',
  Playground = 'Playground',
}
